import API from './api';

export const hfService = {
  // Datasets
  uploadDataset: (file, name) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', name);
    return API.post('/api/hf/datasets/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getDatasets: () => API.get('/api/hf/datasets/'),
  getDataset: (id) => API.get(`/api/hf/datasets/${id}/`),
  deleteDataset: (id) => API.delete(`/api/hf/datasets/${id}/`),

  // Analytics
  // The Overview tab fires summary + cumulative + strategy-summary together. For a large strategy
  // (e.g. Multi-Strategy, ~743 funds) the summary recompute (trailing-window metrics per fund) can
  // exceed the default 30s axios timeout — so the client aborted while the server was still computing
  // (and caching) the result, showing "timeout of 30000ms exceeded". Override to 120s (the backend
  // gunicorn cap) so the FIRST load completes and warms the 24h cache; later loads with the same
  // params are then served instantly from cache.
  getSummary: (params) => API.post('/api/hf/analytics/summary/', params, { timeout: 120000 }),
  getCumulative: (params) => API.post('/api/hf/analytics/cumulative/', params, { timeout: 120000 }),
  getCorrelation: (params) => API.post('/api/hf/analytics/correlation/', params),
  getRolling: (params) => API.post('/api/hf/analytics/rolling/', params),
  getDrawdown: (params) => API.post('/api/hf/analytics/drawdown/', params),
  getCalendar: (params) => API.post('/api/hf/analytics/calendar/', params),
  getComparison: (params) => API.post('/api/hf/analytics/comparison/', params),
  getFundDetail: (params) => API.post('/api/hf/analytics/fund-detail/', params),
  getStrategies: (params) => API.post('/api/hf/analytics/strategies/', params),
  getStrategySummary: (params) => API.post('/api/hf/analytics/strategy-summary/', params, { timeout: 120000 }),

  // HF Overview "Make Default" — persist the user's preferred default Focus Fund (★ button).
  setDefaultFocusFund: (fund) => API.post('/api/hf/default-focus-fund/', { fund }),

  // Data management
  generateTemplate: (datasetId, funds, newFunds) => {
    const params = new URLSearchParams();
    if (funds.length) params.set('funds', funds.join(','));
    if (newFunds.length) params.set('new_funds', newFunds.map(f => f.strategy ? `${f.name}:${f.strategy}` : f.name).join(','));
    return API.get(`/api/hf/datasets/${datasetId}/template/?${params.toString()}`, { responseType: 'blob' });
  },
  previewUpload: (datasetId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return API.post(`/api/hf/datasets/${datasetId}/upload-update/preview/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  commitUpload: (datasetId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return API.post(`/api/hf/datasets/${datasetId}/upload-update/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Direct Bloomberg MV (NAV) bulk upload
  // Heavy: parses ~4k funds x ~300 date cols then diffs all stored NAVs.
  // Override the default 30s axios timeout; backend is capped at 120s.
  previewNavUpload: (datasetId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return API.post(`/api/hf/datasets/${datasetId}/nav-upload/preview/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 180000,
    });
  },
  // Commit the NAV upload. mode = 'upsert' | 'replace'. Returns {job_id}.
  commitNavUpload: (datasetId, file, mode) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('mode', mode);
    return API.post(`/api/hf/datasets/${datasetId}/nav-upload/commit/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 180000,
    });
  },
  getNavUploadJob: (jobId) => API.get(`/api/hf/nav-upload/jobs/${jobId}/`),

  // Benchmark (SPX) total-return INDEX upload — two columns: date + TR-index level.
  // Levels → monthly returns; preview diffs against stored benchmark, commit upserts (synchronous).
  previewBenchmarkUpload: (datasetId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return API.post(`/api/hf/datasets/${datasetId}/benchmark-upload/preview/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  commitBenchmarkUpload: (datasetId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return API.post(`/api/hf/datasets/${datasetId}/benchmark-upload/commit/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Fund profile (terms, people, strategy, custom fields)
  getFundProfile: (fundId) => API.get(`/api/hf/funds/${fundId}/profile/`),
  updateFundProfile: (fundId, data) => API.patch(`/api/hf/funds/${fundId}/profile/`, data),

  // Meeting notes
  listFundNotes: (fundId) => API.get(`/api/hf/funds/${fundId}/notes/`),
  createFundNote: (fundId, data) => API.post(`/api/hf/funds/${fundId}/notes/`, data),
  updateFundNote: (noteId, data) => API.patch(`/api/hf/notes/${noteId}/`, data),
  deleteFundNote: (noteId) => API.delete(`/api/hf/notes/${noteId}/`),

  // Attachments
  uploadNoteAttachment: (noteId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return API.post(`/api/hf/notes/${noteId}/attachments/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteNoteAttachment: (attachmentId) => API.delete(`/api/hf/attachments/${attachmentId}/`),
};
